import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [sessionId, setSessionId] = useState(localStorage.getItem('sessionId') || generateSessionId());
  const { token } = useAuth();

  function generateSessionId() {
    const id = 'session_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('sessionId', id);
    return id;
  }

  const fetchCart = useCallback(async () => {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : { params: { session_id: sessionId } };
      const response = await axios.get(`${API}/cart`, config);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    }
  }, [token, sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(async (product, quantity = 1) => {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const data = {
        product_id: product.id,
        quantity,
        price: product.price
      };
      if (!token) {
        data.session_id = sessionId;
      }
      await axios.post(`${API}/cart`, data, config);
      await fetchCart();
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  }, [token, sessionId, fetchCart]);

  const removeFromCart = useCallback(async (productId) => {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : { params: { session_id: sessionId } };
      await axios.delete(`${API}/cart/${productId}`, config);
      await fetchCart();
    } catch (error) {
      console.error('Failed to remove from cart:', error);
    }
  }, [token, sessionId, fetchCart]);

  const clearCart = useCallback(async () => {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : { params: { session_id: sessionId } };
      await axios.delete(`${API}/cart`, config);
      setCart({ items: [] });
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  }, [token, sessionId]);

  const getCartTotal = useCallback(() => {
    return cart.items?.reduce((total, item) => total + (item.price * item.quantity), 0) || 0;
  }, [cart.items]);

  const getCartCount = useCallback(() => {
    return cart.items?.reduce((count, item) => count + item.quantity, 0) || 0;
  }, [cart.items]);

  const value = useMemo(() => ({ 
    cart, 
    addToCart, 
    removeFromCart, 
    clearCart, 
    getCartTotal, 
    getCartCount, 
    fetchCart 
  }), [cart, addToCart, removeFromCart, clearCart, getCartTotal, getCartCount, fetchCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
